import {
    Avatar,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Skeleton,
    tooltipClasses,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendar, TbUser, TbUserScan } from "react-icons/tb";
import { z } from "zod";

import { TableHeader } from "@/components/TableHeader";
import { clinicArrivalPassengerTypes } from "@/constants/clinicArrivalPassengerTypes";
import { IHotelClinicPassengerType } from "@/models/hotel-clinic.model";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import { HotelClinicListCustomAuditInfoSchema } from "@/schemas/hotelClinicSchema";

import { HotelClinicListItem } from "../../types";

type CreateInfo = z.infer<typeof HotelClinicListCustomAuditInfoSchema>;

type PassengerTypeTooltipContentProps = {
    status: (typeof clinicArrivalPassengerTypes)[number];
    createInfo?: CreateInfo;
};

function PassengerTypeTooltipContent({ status, createInfo }: PassengerTypeTooltipContentProps) {
    const { t } = useTranslation();

    const formattedDate = createInfo
        ? new Date(createInfo.date).toLocaleString("tr-TR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : null;

    return (
        <div className="flex flex-col min-w-52 py-1 text-sm">
            <div
                className="flex items-center gap-2 px-3 py-2 font-medium"
                style={{ color: status.text, backgroundColor: status.bg }}
            >
                {createElement(status.component(), { fontSize: 15, color: status.text })}
                <span>{t(status.label)}</span>
            </div>

            <Divider className="!bg-gray-100" />

            <p className="px-3 py-2 text-xs text-slate-500 leading-snug">{t(status.tooltip.description)}</p>

            {createInfo && (
                <>
                    <Divider className="!bg-gray-100 !w-[85%] !mx-auto" />
                    <List dense sx={{ padding: "0px !important" }}>
                        <ListItem>
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar sx={{ width: 28, height: 28, backgroundColor: "#e0e7ff" }}>
                                    <TbUser size={14} color="#4f46e5" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={createInfo.userName}
                                secondary="Oluşturan"
                                primaryTypographyProps={{ style: { fontSize: 12, fontWeight: 500 } }}
                                secondaryTypographyProps={{ style: { fontSize: 11 } }}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar sx={{ width: 28, height: 28, backgroundColor: "#dcfce7" }}>
                                    <TbCalendar size={14} color="#16a34a" />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={formattedDate}
                                secondary="Oluşturma Tarihi"
                                primaryTypographyProps={{ style: { fontSize: 12, fontWeight: 500 } }}
                                secondaryTypographyProps={{ style: { fontSize: 11 } }}
                            />
                        </ListItem>
                    </List>
                </>
            )}
        </div>
    );
}

export function ClinicArrivalPassengerTypeColumnCellSkeleton({ className }: { className?: string }) {
    return (
        <div className={`flex items-center py-1 h-[40px] ${className || ""}`}>
            <Skeleton variant="rounded" width={70} height={25} className="!rounded-full" />
        </div>
    );
}

export function ClinicArrivalPassengerTypeColumnCell({
    value,
    createInfo,
    className,
}: {
    value: IHotelClinicPassengerType;
    createInfo?: CreateInfo;
    className?: string;
}) {
    const { t } = useTranslation();
    const status = clinicArrivalPassengerTypes.find((item) => item.value === value);

    return (
        <div className={`flex items-center py-1 h-full ${className || ""}`}>
            {status && (
                <NoMaxWidthTooltip
                    arrow
                    sx={{
                        [`& .${tooltipClasses.tooltip}`]: {
                            backgroundColor: "white",
                            border: "1px solid #f1f5f9",
                            padding: "0px !important",
                            fontSize: 12,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                    }}
                    title={<PassengerTypeTooltipContent status={status} createInfo={createInfo} />}
                >
                    <Chip
                        sx={{
                            backgroundColor: status.bg,
                            color: status.text,
                            fontWeight: 500,
                            fontSize: "12px !important",
                        }}
                        icon={createElement(status.component(), {
                            fontSize: 14,
                            color: status.text,
                        })}
                        label={t(status.label || "")}
                    />
                </NoMaxWidthTooltip>
            )}
        </div>
    );
}

export const ClinicArrivalPassengerTypeColumn = ({ t }: { t: TFunction }): GridColDef<HotelClinicListItem> => {
    return {
        field: "type",
        headerName: t("passengerType"),
        minWidth: 140,
        valueGetter: (_val, row) => row.type,
        renderHeader: (params) => <TableHeader Component={TbUserScan} title={params.colDef.headerName!} />,
        renderCell: ({ value, row }) => (
            <ClinicArrivalPassengerTypeColumnCell
                value={value}
                createInfo={row.type === IHotelClinicPassengerType.CUSTOM ? row.createInfo : undefined}
            />
        ),
    };
};

export default ClinicArrivalPassengerTypeColumn;
